/**
 * Styling for the search field inside `SearchableSelect` /
 * `SearchableMultiSelect`'s popover (#13211, Stage 4 slice 9 of #13197).
 *
 * Extracted rather than inlined because `searchable-select.tsx` sits against
 * the 1000-line `check:file-size-ratchet` cap, and this is the part of it that
 * is genuinely a separate concern: the field's presentation, and the four
 * package facts that make each override load-bearing.
 *
 * These classes are layered over `@sai/ui`'s `SearchInput`, which
 * merges a consumer `className` LAST through tailwind-merge — so each entry
 * below DISPLACES the package's conflicting class rather than racing it on
 * stylesheet order.
 *
 *   - `text-base`: the package's `inputVariants` base string is `text-sm`
 *     (14px) at EVERY size — its size variants change only height and padding
 *     — and #7918 put a hard 16px floor on form controls because iOS Safari
 *     auto-zooms the viewport below it. The `no-small-font-on-form-input`
 *     lint rule cannot see through a `<SearchInput>` element name, so the
 *     floor is restated here rather than left to the guard.
 *
 *   - the focus ring: the package's treatment is
 *     `focus-visible:shadow-focus-inset`, which `src/tokens/sai-ui-names.css`
 *     keeps INERT since #15994 (it paints no shadow; Cadence draws focus with
 *     its own ring), so without a replacement the auto-focused field would
 *     have no focus indicator at all. `ring-offset-2` is deliberately absent
 *     from the replacement: an offset band only matches when its colour
 *     tracks the ground, and the replacement ring needs no gap.
 *
 *   - the `::-webkit-search-cancel-button` / `::-webkit-search-decoration`
 *     reset: `SearchInput` hardcodes `type="search"` on its inner input and
 *     omits `type` from its public props, so Chrome and Safari paint their
 *     OWN native clear "×" as soon as the field has a value. `hideClear`
 *     suppresses only the package's React clear button, not the browser's,
 *     and it also drops the package's `pe-8` clear-padding — so the native
 *     control would sit flush against the field edge. The package does ship
 *     this reset, but in `dist/styles/index.css`, which
 *     `src/tokens/sai-ui-names.css:20` records that this repo deliberately
 *     does NOT import (its palette and global `@layer base` rules would
 *     restyle every page). Restating it as a utility keeps the fix inside the
 *     component rather than requiring a global-stylesheet edit.
 *
 *     No unit test can cover this one: happy-dom renders no native shadow
 *     control, so a `getAllByRole('button')` count cannot see the "×" it
 *     would be written to catch.
 */
export const SEARCH_FIELD_CLASSES =
  'text-base focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-stroke-selected ' +
  '[&::-webkit-search-cancel-button]:appearance-none [&::-webkit-search-cancel-button]:hidden ' +
  '[&::-webkit-search-decoration]:appearance-none [&::-webkit-search-decoration]:hidden'
